/**
 * useTheme — Dark-Mode-State (light | dark | system).
 *
 * Pattern: Linear/Vercel-Style.
 * - User-Pref persistiert in localStorage (Key: mcms-theme).
 * - 'system' beobachtet `prefers-color-scheme`.
 * - Effective Theme wird als `.dark`-Class an `<html>` gesetzt.
 * - meta theme-color synct mit aktivem Theme (für mobile Status-Bar).
 */
import { computed, readonly, watchEffect } from 'vue'
import { useMediaQuery, useStorage } from '@vueuse/core'

export type ThemePref = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

const STORAGE_KEY = 'mcms-theme'

const pref = useStorage<ThemePref>(STORAGE_KEY, 'system')
const systemDark = useMediaQuery('(prefers-color-scheme: dark)')

const effective = computed<EffectiveTheme>(() => {
  if (pref.value === 'system') return systemDark.value ? 'dark' : 'light'
  return pref.value
})

watchEffect(() => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('dark', effective.value === 'dark')

  // Sync mobile status-bar color
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) {
    meta.content = effective.value === 'dark' ? '#0d1117' : '#2260d2'
  }
})

export function useTheme() {
  function setTheme(next: ThemePref): void {
    pref.value = next
  }

  // Binär: kippt aufs jeweils andere Theme (relativ zum aktuell sichtbaren).
  // 'system' bleibt als unsichtbarer Default (= Gerät) erhalten, ist aber nicht
  // mehr per Klick wählbar — sobald geklickt wird, ist es explizit light/dark.
  function cycle(): void {
    pref.value = effective.value === 'dark' ? 'light' : 'dark'
  }

  return {
    pref,
    effective: readonly(effective),
    setTheme,
    cycle,
  }
}
